import React, { useRef, forwardRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Transition } from 'react-transition-group'
import { createUseStyles } from 'react-jss'
import useForkRef from '../../utils/useForkRef'

const baseStyles = (props) => ({
	/* Styles applied to the root element. */
	height: 0,
	overflow: 'hidden',
	transition: 'height',
	'-webkit-transition': 'height',
	'-moz-transition': 'height',
	'-o-transition': 'height',
	...(props.orientation === 'horizontal' && {
		height: 'auto',
		width: 0,
		transition: 'width',
		'-webkit-transition': 'width',
		'-moz-transition': 'width',
		'-o-transition': 'width'
	}),
	/* Styles applied to the root element when the transition has entered. */
	...(props.state === 'entered' && {
		height: 'auto',
		overflow: 'visible',
		...(props.orientation === 'horizontal' && {
			width: 'auto'
		})
	}),
	/* Styles applied to the root element when the transition has exited and `collapsedSize` = 0px. */
	...(props.state === 'exited' &&
		!props.in &&
		props.collapsedSize === '0px' && {
		visibility: 'hidden'
	})
})

const wrapperStyles = (props) => ({
	/* Styles applied to the outer wrapper element. */
	// Hack to get children with a negative margin to not falsify the height computation.
	display: 'flex',
	width: '100%',
	...(props.orientation === 'horizontal' && {
		width: 'auto',
		height: '100%'
	}),
	...(props.wrapperStyles && props.wrapperStyles)
})

const wrapperInnerStyles = (props) => ({
	/* Styles applied to the inner wrapper element. */
	width: '100%',
	...(props.orientation === 'horizontal' && {
		width: 'auto',
		height: '100%'
	}),
	...(props.wrapperInnerStyles && props.wrapperInnerStyles)
})

const styles = {
	baseStyles: (props) => baseStyles(props),
	wrapperStyles: (props) => wrapperStyles(props),
	wrapperInnerStyles: (props) => wrapperInnerStyles(props)
}
const useStyles = createUseStyles(styles, { name: 'Vb' })

function getAutoHeightDuration(height) {
	if (!height) {
		return 0
	}

	const constant = height / 36

	// https://www.wolframalpha.com/input/?i=(4+%2B+15+*+(x+%2F+36+)+**+0.25+%2B+(x+%2F+36)+%2F+5)+*+10
	return Math.round((4 + 15 * constant ** 0.25 + constant / 5) * 10)
}

function getTransitionProps(props, options) {
	const { timeout, easing, style = {} } = props

	return {
		duration: style.transitionDuration || typeof timeout === 'number' ? timeout : timeout[options.mode] || 0,
		easing: style.transitionTimingFunction || typeof easing === 'object' ? easing[options.mode] : easing,
		delay: style.transitionDelay
	}
}

const Collapse = forwardRef(function Collapse(props, ref) {
	const {
		appear: appearProp,
		children,
		className,
		collapsedSize: collapsedSizeProp = '0px',
		component,
		easing,
		in: inProp,
		onEnter,
		onEntered,
		onEntering,
		onExit,
		onExited,
		onExiting,
		orientation = 'vertical',
		style,
		timeout,
		// eslint-disable-next-line react/prop-types
		TransitionComponent = Transition,
		...other
	} = props

	const classes = useStyles(props)
	const timer = useRef()
	const wrapperRef = useRef(null)
	const autoTransitionDuration = useRef()
	const collapsedSize = typeof collapsedSizeProp === 'number' ? `${collapsedSizeProp}px` : collapsedSizeProp
	const isHorizontal = orientation === 'horizontal'
	const size = isHorizontal ? 'width' : 'height'

	useEffect(() => {
		return () => {
			clearTimeout(timer.current)
		}
	}, [])

	const nodeRef = React.useRef(null)
	const handleRef = useForkRef(ref, nodeRef)

	const normalizedTransitionCallback = (callback) => (maybeIsAppearing) => {
		if (callback) {
			const node = nodeRef.current

			// onEnterXxx and onExitXxx callbacks have a different arguments.length value.
			if (maybeIsAppearing === undefined) {
				callback(node)
			} else {
				callback(node, maybeIsAppearing)
			}
		}
	}

	const getWrapperSize = () =>
		wrapperRef.current ? wrapperRef.current[isHorizontal ? 'clientWidth' : 'clientHeight'] : 0

	const handleEnter = normalizedTransitionCallback((node, isAppearing) => {
		if (wrapperRef.current && isHorizontal) {
			// Set absolute position to get the size of collapsed content
			wrapperRef.current.style.position = 'absolute'
		}
		node.style[size] = collapsedSize

		if (onEnter) {
			onEnter(node, isAppearing)
		}
	})

	const handleEntering = normalizedTransitionCallback((node, isAppearing) => {
		const wrapperSize = getWrapperSize()

		if (wrapperRef.current && isHorizontal) {
			// After the size is read reset the position back to default
			wrapperRef.current.style.position = ''
		}

		const { duration: transitionDuration, easing: transitionTimingFunction } = getTransitionProps(
			{ style, timeout, easing },
			{
				mode: 'enter'
			}
		)

		if (timeout === 'auto') {
			const duration2 = getAutoHeightDuration(wrapperSize)
			node.style.transitionDuration = `${duration2}ms`
			autoTransitionDuration.current = duration2
		} else {
			node.style.transitionDuration =
				typeof transitionDuration === 'string' ? transitionDuration : `${transitionDuration}ms`
		}

		node.style[size] = `${wrapperSize}px`
		node.style.transitionTimingFunction = transitionTimingFunction

		if (onEntering) {
			onEntering(node, isAppearing)
		}
	})

	const handleEntered = normalizedTransitionCallback((node, isAppearing) => {
		node.style[size] = 'auto'

		if (onEntered) {
			onEntered(node, isAppearing)
		}
	})

	const handleExit = normalizedTransitionCallback((node) => {
		node.style[size] = `${getWrapperSize()}px`

		if (onExit) {
			onExit(node)
		}
	})

	const handleExited = normalizedTransitionCallback(onExited)

	const handleExiting = normalizedTransitionCallback((node) => {
		const wrapperSize = getWrapperSize()
		const { duration: transitionDuration, easing: transitionTimingFunction } = getTransitionProps(
			{ style, timeout, easing },
			{ mode: 'exit' }
		)

		if (timeout === 'auto') {
			// TODO: rename getAutoHeightDuration to something more generic (width support)
			// Actually it just calculates animation duration based on size
			const duration2 = getAutoHeightDuration(wrapperSize)
			node.style.transitionDuration = `${duration2}ms`
			autoTransitionDuration.current = duration2
		} else {
			node.style.transitionDuration =
				typeof transitionDuration === 'string' ? transitionDuration : `${transitionDuration}ms`
		}

		node.style[size] = collapsedSize
		node.style.transitionTimingFunction = transitionTimingFunction

		if (onExiting) {
			onExiting(node)
		}
	})

	const addEndListener = (next) => {
		if (timeout === 'auto') {
			timer.current = setTimeout(next, autoTransitionDuration.current || 0)
		}
	}

	return (
		<TransitionComponent
			in={inProp}
			appear={appearProp}
			onEnter={handleEnter}
			onEntered={handleEntered}
			onEntering={handleEntering}
			onExit={handleExit}
			onExited={handleExited}
			onExiting={handleExiting}
			addEndListener={addEndListener}
			nodeRef={nodeRef}
			timeout={timeout === 'auto' ? null : timeout}
			{...other}
		>
			<div //CollapseWrapper Root
				as={component}
				className={clsx(classes.baseStyles, className)}
				style={{
					[isHorizontal ? 'minWidth' : 'minHeight']: collapsedSize,
					...style
				}}
				ref={handleRef}
			>
				<div //CollapseWrapper
					className={classes.wrapper}
					ref={wrapperRef}
				>
					<div //CollapseWrapperInner
						className={classes.wrapperInner}
					>
						{children}
					</div>
				</div>
			</div>
		</TransitionComponent>
	)
})

export default Collapse

Collapse.defaultProps = {
	collapsedSize: '0px',
	orientation: 'vertical'
}

Collapse.propTypes = {
	/**
	 * The content node to be collapsed.
	 */
	children: PropTypes.node,
	/**
	 * Override or extend the styles applied to the component.
	 */
	classes: PropTypes.object,
	/**
	 * @ignore
	 */
	className: PropTypes.string,
	/**
	 * The width (horizontal) or height (vertical) of the container when collapsed.
	 * @default '0px'
	 */
	collapsedSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	/**
	 * The component used for the root node.
	 * Either a string to use a HTML element or a component.
	 */
	component: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.node]),
	/**
	 * The transition timing function.
	 * You may specify a single easing or a object containing enter and exit values.
	 */
	easing: PropTypes.oneOfType([
		PropTypes.shape({
			enter: PropTypes.string,
			exit: PropTypes.string
		}),
		PropTypes.string
	]),
	/**
	 * If `true`, the component will transition in.
	 */
	in: PropTypes.bool,
	/**
	 * @ignore
	 */
	onEnter: PropTypes.func,
	/**
	 * @ignore
	 */
	onEntered: PropTypes.func,
	/**
	 * @ignore
	 */
	onEntering: PropTypes.func,
	/**
	 * @ignore
	 */
	onExit: PropTypes.func,
	/**
	 * @ignore
	 */
	onExited: PropTypes.func,
	/**
	 * @ignore
	 */
	onExiting: PropTypes.func,
	/**
	 * The transition orientation.
	 * @default 'vertical'
	 */
	orientation: PropTypes.oneOf(['horizontal', 'vertical']),
	/**
	 * @ignore
	 */
	style: PropTypes.object,
	/**
	 * The duration for the transition, in milliseconds.
	 * You may specify a single timeout for all transitions, or individually with an object.
	 *
	 * Set to 'auto' to automatically calculate transition time based on height.
	 * @default duration.standard
	 */
	timeout: PropTypes.oneOfType([
		PropTypes.oneOf(['auto']),
		PropTypes.number,
		PropTypes.shape({
			appear: PropTypes.number,
			enter: PropTypes.number,
			exit: PropTypes.number
		})
	])
}
